/** Minimum length for a strong password. */
export const PASSWORD_MIN_LENGTH = 10;

/** Allowed characters: English letters, digits, common symbols. */
const ENGLISH_PASSWORD_REGEX =
  /^[A-Za-z0-9@#$%^&*()_+\-=[\]{}|;:'",.<>?/`~\\!]+$/;

const SYMBOL_REGEX = /[@#$%^&*()_+\-=[\]{}|;:'",.<>?/`~\\!]/;

/** Known weak passwords (compared case-insensitively). */
const COMMON_PASSWORDS = new Set(
  [
    "admin123",
    "admin@123",
    "Admin@123",
    "123456@admin",
    "123456",
    "password",
    "password1",
    "Password1",
    "Password@1",
    "qwerty123",
    "letmein",
    "welcome1",
    "abc123",
    "111111",
    "12345678",
    "123456789",
  ].map((p) => p.toLowerCase())
);

export const getPasswordChecks = (password) => {
  const normalized = (password || "").trim();

  return {
    minLength: normalized.length >= PASSWORD_MIN_LENGTH,
    englishOnly: !normalized || ENGLISH_PASSWORD_REGEX.test(normalized),
    lower: /[a-z]/.test(normalized),
    upper: /[A-Z]/.test(normalized),
    digit: /\d/.test(normalized),
    special: SYMBOL_REGEX.test(normalized),
    notCommon: !normalized || !COMMON_PASSWORDS.has(normalized.toLowerCase()),
  };
};

export const getPasswordStrength = (password) => {
  if (!password) {
    return { score: 0, checks: getPasswordChecks("") };
  }

  const checks = getPasswordChecks(password);
  const passed = Object.values(checks).filter(Boolean).length;
  const total = Object.keys(checks).length;
  const score = Math.min(4, Math.floor((passed / total) * 5));

  return { score, checks };
};

export const isPasswordStrong = (password) => {
  const checks = getPasswordChecks(password);
  return (
    checks.minLength &&
    checks.englishOnly &&
    checks.lower &&
    checks.upper &&
    checks.digit &&
    checks.special &&
    checks.notCommon
  );
};

export const canSubmitPasswordChange = ({
  currentPassword,
  newPassword,
  confirmPassword,
}) => {
  if (!currentPassword?.trim() || !newPassword?.trim() || !confirmPassword?.trim()) {
    return false;
  }
  if (newPassword !== confirmPassword) {
    return false;
  }
  return isPasswordStrong(newPassword);
};

export const STRENGTH_LABEL_KEYS = [
  "accountSettings.passwordStrength.weak",
  "accountSettings.passwordStrength.fair",
  "accountSettings.passwordStrength.good",
  "accountSettings.passwordStrength.strong",
  "accountSettings.passwordStrength.veryStrong",
];

export const STRENGTH_COLORS = [
  "#f44336",
  "#ff9800",
  "#ffc107",
  "#8bc34a",
  "#4caf50",
];
